import { JSONContent } from "@tiptap/core";

export enum ActivityEventType {
  // Member-related events
  MEMBER_ADDED = "Member Added",
  MEMBER_REMOVED = "Member Removed",
  MEMBER_ROLE_UPDATED = "Member Role Updated",
  MEMBER_LOGGED_IN = "Member Logged In",
  MEMBER_LOGGED_OUT = "Member Logged Out",
  MEMBER_ONLINE_STATUS_CHANGED = "Member Online Status Changed",
  MEMBER_ENABLED_2FA = "Member Enabled 2FA",
  MEMBER_DISABLED_2FA = "Member Disabled 2FA",
  MEMBER_PASSWORD_RESET = "Member Password Reset",
  MEMBER_PROFILE_UPDATED = "Member Profile Updated",

  // Team-related events
  TEAM_NAME_CHANGED = "Team Name Changed",
  TEAM_FEATURE_ADDED = "Team Feature Added",
  TEAM_FEATURE_REMOVED = "Team Feature Removed",
  TEAM_MEMBER_ADDED = "Team Member Added",

  // Status-related events
  STATUS_ADDED = "Status Added",
  STATUS_REMOVED = "Status Removed",
  STATUS_PARAMETER_CHANGED = "Status Parameter Changed",

  // Workflow-related events
  WORKFLOW_CREATED = "Workflow Created",
  WORKFLOW_SET_AS_DEFAULT = "Workflow Set as Default",

  // Project-related events
  MEMBER_ADDED_TO_PROJECT = "Member Added to Project",
  MEMBER_REMOVED_FROM_PROJECT = "Member Removed from Project",
  NEW_PROJECT_CREATED = "New Project Created",
  PROJECT_NAME_UPDATED = "Project Name Updated",
  PROJECT_STATUS_UPDATED = "Project Status Updated",

  // Ticket-related events
  TICKET_CREATED = "Ticket Created",
  TICKET_UPDATED = "Ticket Updated",
  TICKET_ARCHIVED = "Ticket Archived",
  TICKET_STATUS_UPDATED = "Ticket Status Updated",
  COMMENT_ADDED = "Comment Added",
  COMMENT_DELETED = "Comment Deleted",
  COMMENT_UPDATED = "Comment Updated",
  ATTACHMENT_UPLOADED = "Attachment Uploaded",
  ATTACHMENT_DELETED = "Attachment Deleted",
  TICKET_PRIORITY_UPDATED = "Ticket Priority Updated",
  TICKET_TITLE_UPDATED = "Ticket Title Updated",
  TICKET_DESCRIPTION_UPDATED = "Ticket Description Updated",
  TICKET_TYPE_UPDATED = "Ticket Type Updated",

  // TICKET_TAG_UPDATED = "Ticket Tag Updated",
  TICKET_TAG_ADDED = "Ticket Tag Added",
  TICKET_TAG_REMOVED = "Ticket Tag Removed",
  TICKET_ASSIGNEE_UPDATED = "Ticket Assignee Updated",
  TICKET_DUE_DATE_UPDATED = "Ticket Due Date Updated",
  TICKET_IMPORTED = "Ticket Imported",
  TICKET_EXPORTED = "Ticket Exported",
  TICKET_TYPE_ADDED = "Ticket Type Added",
  TICKET_CUSTOM_FIELD_ADDED = "Ticket Custom Field Added",
  TICKET_CUSTOM_FIELD_NAME_UPDATED = "Ticket Custom Field Name Updated",
  TAG_CREATED = "Tag Created",
  TAG_UPDATED = "Tag Updated",
  TAG_DELETED = "Tag Deleted",
  TICKET_ESTIMATED_TIME_UPDATED = "Ticket Estimated Time Updated",
  TICKET_LINKED = "Ticket Linked",
  TICKET_UNLINKED = "Ticket Unlinked",
  TICKET_HEADER_FIELD_TURNED_ON = "Header Field Turned On",
  TICKET_HEADER_FIELD_TURNED_OFF = "Header Field Turned Off",

  PROJECT_CREATED = "Project Created",

  //ARCHIVE_TICKET
  ARCHIVE_TICKET_TAG = "Archive Ticket Tag",
  ARCHIVE_TICKET_STATUS = "Archive Ticket Status",
  ARCHIVE_BOARD = "Archive Board",
  ARCHIVE_PROJECT = "Archive Project",

  RESTORE_FROM_ARCHIVE = "Restore from archive",
  ARCHIVE_ITEM = "Archive Item",

  // Sprint-related events
  NEW_SPRINT_CREATED = "New Sprint Created",
  SPRINT_CREATED = "Sprint Created",
  SPRINT_STATUS_UPDATED = "Sprint Status Updated",
  SPRINT_DURATION_MODIFIED = "Sprint Duration Modified",
  SPRINT_RETROSPECTIVE_RECEIVED = "Sprint Retrospective Received",

  SPRINT_ASSIGNEE_ADDED = "Assignee Added to Sprint",
  SPRINT_ASSIGNEE_REMOVED = "Assignee Removed from Sprint",

  SPRINT_TICKET_ADDED = "Ticket Added to Sprint",
  SPRINT_TICKET_REMOVED = "Ticket Removed from Sprint",

  // Time tracking-related events
  TIME_ENTRY_ADDED = "Time Entry Added",
  TIME_ENTRY_CHANGED = "Time Entry Changed",
  TIME_ENTRY_DELETED = "Time Entry Deleted",
  TIME_TRACKING_STARTED = "Time Tracking Started",
  TIME_TRACKING_STOPPED = "Time Tracking Stopped",

  // Board-related events
  NEW_BOARD_CREATED = "New Board Created",
  BOARD_SETTINGS_SAVED = "Board Settings Saved",

  COMMENT_REPLIED = "Comment Replied",

  NOTIFICATION_ENABLE = "Notification Enabled",
  NOTIFICATION_DISABLE = "Notification Disabled",
}

export const activityConfig: Record<
  string,
  { template: string; label: string }
> = {
  // --- Team Member ---
  [ActivityEventType.MEMBER_ADDED]: {
    label: "Member Added",
    template: "New member added in the team",
  },
  [ActivityEventType.MEMBER_ROLE_UPDATED]: {
    label: "Member Role Updated",
    template: "{member_name}'s role updated from {old_role} to {new_role}",
  },
  [ActivityEventType.MEMBER_REMOVED]: {
    label: "Member Removed",
    template: "A member was removed from the team",
  },
  [ActivityEventType.MEMBER_LOGGED_IN]: {
    label: "Member Logged In",
    template: "{member_name} logged in to team {team_name}",
  },
  "Member Fail to Log In": {
    label: "Member Fail to Log In",
    template: "{member_name} failed log in to team {team_name}",
  },
  [ActivityEventType.MEMBER_LOGGED_OUT]: {
    label: "Member Logged Out",
    template: "{member_name} logged out from team {team_name}",
  },
  [ActivityEventType.MEMBER_ONLINE_STATUS_CHANGED]: {
    label: "Member Online Status Changed",
    template: "{member_name} is now {online_status} in team {team_name}",
  },
  [ActivityEventType.TEAM_NAME_CHANGED]: {
    label: "Team Name Changed",
    template: "Team name changed from {old_team_name} to {new_team_name}",
  },
  [ActivityEventType.TEAM_FEATURE_ADDED]: {
    label: "Team Feature Added",
    template: "{feature_name} feature was added to team {team_name}",
  },
  [ActivityEventType.TEAM_FEATURE_REMOVED]: {
    label: "Team Feature Removed",
    template: "{feature_name} feature was removed from team {team_name}",
  },
  [ActivityEventType.MEMBER_ENABLED_2FA]: {
    label: "Member Enabled 2FA",
    template: "enabled two-factor authentication",
  },
  [ActivityEventType.MEMBER_DISABLED_2FA]: {
    label: "Member Disabled 2FA",
    template: "disabled two-factor authentication",
  },
  [ActivityEventType.MEMBER_PASSWORD_RESET]: {
    label: "Member Password Reset",
    template: "password was reset",
  },
  [ActivityEventType.MEMBER_PROFILE_UPDATED]: {
    label: "Member Profile Updated",
    // template:
    //   "updated their profile parameter '{profile_parameter_name}', old value = '{oldName}', new value = '{newName}'",
    template:
      "updated their profile parameter from old value ='{oldName}'to new value = '{newName}'",
  },

  // --- WorkFlow ---
  [ActivityEventType.STATUS_ADDED]: {
    label: "Status Added",
    template: "New status '{status_name}' added to workflow '{workflow_name}'",
  },
  [ActivityEventType.STATUS_REMOVED]: {
    label: "Status Removed",
    template: "Status '{status_name}' removed from workflow '{workflow_name}'",
  },
  [ActivityEventType.STATUS_PARAMETER_CHANGED]: {
    label: "Status Parameter Changed",
    template:
      "Parameters for status '{status_name}' updated parameter '{parameter_name}', old value = '{old_value}', new value = '{new_value}' in workflow '{workflow_name}'",
  },
  "Transition Added": {
    label: "Transition Added",
    template:
      "New transition '{transition_name}' added to status '{status_name}' in workflow '{workflow_name}'",
  },
  "Transition Updated": {
    label: "Transition Updated",
    template:
      "Transition '{transition_name}' in status '{status_name}' updated parameter '{parameter_name}', old value = '{old_value}', new value = '{new_value}'",
  },
  "Transition Removed": {
    label: "Transition Removed",
    template:
      "Transition '{transition_name}' removed from status '{status_name}' in workflow '{workflow_name}'",
  },
  "Transition Rule Added": {
    label: "Transition Rule Added",
    template:
      "New rule added to transition '{transition_name}' in status '{status_name}' of workflow '{workflow_name}'",
  },
  "Transition Rule Removed": {
    label: "Transition Rule Removed",
    template:
      "Rule removed from transition '{transition_name}' in status '{status_name}' of workflow '{workflow_name}'",
  },
  "Transition Rule Changed": {
    label: "Transition Rule Changed",
    template:
      "Rule for transition '{transition_name}' in status '{status_name}' updated parameter '{parameter_name}', old value = '{old_value}', new value = '{new_value}'",
  },
  [ActivityEventType.WORKFLOW_CREATED]: {
    label: "Workflow Created",
    template: "New workflow '{workflow_name}' created",
  },
  [ActivityEventType.WORKFLOW_SET_AS_DEFAULT]: {
    label: "Workflow Set as Default",
    template: "Workflow '{workflow_name}' set as default",
  },

  // --- Project ---
  [ActivityEventType.MEMBER_ADDED_TO_PROJECT]: {
    label: "Member Added to Project",
    template: "{member_name} was added to project {projectName}",
  },
  [ActivityEventType.MEMBER_REMOVED_FROM_PROJECT]: {
    label: "Member Removed from Project",
    template: "{member_name} was removed from project {projectName}",
  },
  [ActivityEventType.NEW_PROJECT_CREATED]: {
    label: "New Project Created",
    template: "New project '{projectName}' created",
  },
  [ActivityEventType.PROJECT_NAME_UPDATED]: {
    label: "Project Name Updated",
    template:
      "Project name updated, old value = '{old_project_name}', new value = '{new_project_name}'",
  },
  [ActivityEventType.PROJECT_STATUS_UPDATED]: {
    label: "Project Status Updated",
    template:
      "Project '{projectName}' updated status, old value = '{oldStatus}', new value = '{newStatus}'",
  },

  // --- Ticket ---
  [ActivityEventType.TICKET_ASSIGNEE_UPDATED]: {
    label: "Assignee Updated",
    template:
      "Ticket '{ticketName}' assignee changed from {oldAssigneeName} to {newAssigneeName}",
  },
  [ActivityEventType.TICKET_CREATED]: {
    label: "Ticket Created",
    template: "New ticket '{ticketName}' created",
  },
  [ActivityEventType.TICKET_STATUS_UPDATED]: {
    label: "Ticket Status Updated",
    template:
      "Ticket '{ticketName}' status changed from {oldStatusName} to {newStatusName}",
  },
  [ActivityEventType.TICKET_TYPE_ADDED]: {
    label: "Ticket Type Added",
    template: "New ticket type '{typeName}' added",
  },
  [ActivityEventType.TICKET_TYPE_UPDATED]: {
    label: "Ticket Type Updated",
    template: "Ticket type '{typeName}' was updated",
  },
  [ActivityEventType.COMMENT_ADDED]: {
    label: "Comment Added",
    template: "New comment added to ticket '{ticketName}':",
  },
  [ActivityEventType.COMMENT_DELETED]: {
    label: "Comment Deleted",
    template: "Comment deleted from ticket '{ticketName}'",
  },
  [ActivityEventType.COMMENT_UPDATED]: {
    label: "Comment Updated",
    template: "Comment updated on ticket '{ticketName}'. New content:",
  },
  [ActivityEventType.TICKET_TAG_ADDED]: {
    label: "Ticket Tag Added",
    template: "Tag '{newTagIds}' added to ticket '{ticketName}'",
  },
  [ActivityEventType.TICKET_TAG_REMOVED]: {
    label: "Ticket Tag Removed",
    template: "Tag '{newTagIds}' removed from ticket '{ticketName}'",
  },
  [ActivityEventType.TAG_CREATED]: {
    label: "Tag Created",
    template: "New tag '{tagName}' created",
  },
  [ActivityEventType.TAG_UPDATED]: {
    label: "Tag Updated",
    template: "Tag '{tagName}' updated",
  },
  "Tag Removed": {
    label: "Tag Removed",
    template: "Tag '{tagName}' removed",
  },
  "Ticket Dependency Added": {
    label: "Dependency Updated",
    template:
      "Dependency added: Ticket '{ticketName}' now depends on '{dependent_ticket_name}'",
  },
  "Ticket Dependency Removed": {
    label: "Ticket Dependency Removed",
    template:
      "Dependency removed: Ticket '{ticketName}' no longer depends on '{dependent_ticket_name}'",
  },
  "Ticket Dependency Updated": {
    label: "Ticket Dependency Updated",
    template:
      "Dependency between '{ticketName}' and '{dependent_ticket_name}' was updated",
  },
  [ActivityEventType.TICKET_PRIORITY_UPDATED]: {
    label: "Ticket Priority Updated",
    template:
      "Ticket '{ticketName}' priority changed from {oldPriority} to {newPriority}",
  },
  [ActivityEventType.TICKET_DUE_DATE_UPDATED]: {
    label: "Ticket Due Date Updated",
    template:
      "Ticket '{ticketName}' due date changed from {oldDueDate} to {newDueDate}",
  },
  [ActivityEventType.TICKET_ESTIMATED_TIME_UPDATED]: {
    label: "Ticket Estimated Time Updated",
    template:
      "Ticket '{ticketName}' estimated time updated from {oldTime} to {newTime}",
  },
  [ActivityEventType.TICKET_DESCRIPTION_UPDATED]: {
    label: "Ticket Description Updated",
    template: "Ticket '{ticketName}' description updated",
  },
  "Ticket Linked": {
    label: "Ticket Linked",
    template: "Ticket '{ticketName}' linked to ticket '{linked_ticket_name}'",
  },
  "Ticket Unlinked": {
    label: "Ticket Unlinked",
    template:
      "Ticket '{ticketName}' unlinked from ticket '{unlinked_ticket_name}'",
  },
  [ActivityEventType.ATTACHMENT_UPLOADED]: {
    label: "Attachment Uploaded",
    template: "New attachment '{file_name}' uploaded to ticket '{ticketName}'",
  },
  [ActivityEventType.ATTACHMENT_DELETED]: {
    label: "Attachment Deleted",
    template: "Attachment '{file_name}' deleted from ticket '{ticketName}'",
  },
  [ActivityEventType.TICKET_TITLE_UPDATED]: {
    label: "Ticket Title Updated",
    template: "Ticket {ticketName} updated from {oldTitle} to {newTitle}",
  },
  // --- Releases ---
  "Release Created": {
    label: "Release Created",
    template: "New release '{release_name}' created",
  },
  "Release Approved": {
    label: "Release Approved",
    template: "Release '{release_name}' approved",
  },
  "Release Cancelled": {
    label: "Release Cancelled",
    template: "Release '{release_name}' cancelled",
  },
  "Release Completed": {
    label: "Release Completed",
    template: "Release '{release_name}' completed",
  },

  // --- Sprint ---
  [ActivityEventType.SPRINT_STATUS_UPDATED]: {
    label: "Sprint Status Updated",
    template: "Sprint '{sprintName}' status updated to {newStatus}",
  },
  [ActivityEventType.SPRINT_CREATED]: {
    label: "Sprint Created",
    template: "New sprint '{sprintName}' created",
  },
  [ActivityEventType.SPRINT_DURATION_MODIFIED]: {
    label: "Sprint Duration Modified",
    template: "Sprint '{sprintName}' duration changed to {new_duration}",
  },

  // --- Time Tracking ---
  [ActivityEventType.TIME_ENTRY_ADDED]: {
    label: "Time Entry Added",
    template: "New time entry added for {ticket_or_project_name}",
  },
  "Day-Off Flagged": {
    label: "Day-Off Flagged",
    template: "flagged {date} as a day off",
  },

  // --- Boards ---
  "New Board Created": {
    label: "New Board Created",
    template: "New board '{boardName}' created",
  },
  "Column Added": {
    label: "Column Added",
    template: "New column '{column_name}' added to board '{boardName}'",
  },
  "Work in Progress (WIP) Limit Updated": {
    label: "Work in Progress (WIP) Limit Updated",
    template:
      "WIP limit updated to {new_limit} for column '{column_name}' on board '{boardName}'",
  },
  "Board Settings Saved": {
    label: "Board Settings Saved",
    template: "Board settings for '{boardName}' were updated",
  },

  // --- Import/Export ---
  "Tickets Exported": {
    label: "Tickets Exported",
    template: "{count} tickets exported ({size} MB)",
  },
  "Tickets Imported": {
    label: "Tickets Imported",
    template: "{count} tickets imported ({size} MB)",
  },
  "Users Imported": {
    label: "Users Imported",
    template: "{count} users imported ({size} MB)",
  },

  // --- QA Testing ---
  "Test Plan Created": {
    label: "Test Plan Created",
    template: "New test plan '{test_plan_name}' created",
  },
  "Test Plan Execution Completed": {
    label: "Test Plan Execution Completed",
    template:
      "Test plan '{test_plan_name}' execution completed: {pass_count} passed, {fail_count} failed",
  },

  // --- Archive ---
  [ActivityEventType.ARCHIVE_ITEM]: {
    label: "Archive Item",
    template: "{itemType} ' {itemName}' archived",
  },

  [ActivityEventType.RESTORE_FROM_ARCHIVE]: {
    label: "Restore from archive",
    template: "{itemType} ' {itemName}' restored from archive",
  },

  // Assignee
  "Assignee Added to Sprint": {
    label: "Assignee Added to Sprint",
    template: "Assignee '{assigneeName}' added to sprint '{sprintName}'",
  },

  // Notification
  "Notification Enabled": {
    label: "Notification Enabled",
    template: "Notification '{notificationName}' enabled",
  },

  "Notification Disabled": {
    label: "Notification Disabled",
    template: "Notification '{notificationName}' disabled",
  },
};

export type IEffortForEffortEstimated = {
  hours: number;
  minutes: number;
  date: string;
  _id: string;
};

export type EffortEstimatedType = {
  _id: string;
  effort: IEffortForEffortEstimated;
  isLock: boolean;
  memberId: string;
};

export interface IEffort {
  hours: number;
  minutes: number;
  startTime: string;
  endTime: string;
  date: string;
  _id: string;
}

export type EffortEstimatedPayload = {
  hours: number;
  minutes: number;
  date: string;
};

export enum UploadOptionId {
  UploadFile = "upload_file",
  Dropbox = "dropbox",
  OneDrive = "one_drive",
  GoogleDrive = "google_drive",
  NewGoogleDocMenu = "",
  NewGoogleDoc = "new_google_doc",
  NewGoogleSheet = "new_google_sheet",
}

export type JsonDocType = {
  type?: string;
  content?: JSONContent[];
};

export type AttachmentType = {
  fileId: string;
  filePath: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  url: string;
  createdAt?: string;
  updatedAt?: string;
};
